import { useGetSiteByIdQuery } from '@/apis/sites/sitesApi';
import { DataItems, LabeledIcon } from '@/components';
import { getUSStateName } from '@/types';
import {
  Chip,
  Flag,
  Icon,
  LoadingSpinner,
  Stack,
  TextTruncate,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { FlagsType } from '@data-products-and-ai/react-components/lib/components/DataDisplay/Flag/Flag';
import { FC, useMemo } from 'react';

import '@/pages/Outages/[ID]/components/OutageSite/OutageSite.scss';

const SITE_TOOLTIP_TEXT = `Outage Site Information

This section provides key details about the site where the outage is taking place, including location, site type, customer, and any relevant notes or comments.

Reviewing this information helps ensure proper planning, communication, and safe work execution.`;

interface OutageSiteProps {
  siteId: number;
}

export const OutageSite: FC<OutageSiteProps> = ({ siteId }) => {
  const { data: site, isLoading } = useGetSiteByIdQuery(siteId, {
    skip: !siteId,
  });

  const location = `${site?.city}, ${getUSStateName(site?.state ?? '')}`;

  const dataItems = useMemo(
    () => [
      ...(site?.status
        ? [
            {
              key: 'status',
              label: 'Status',
              value: (
                <Chip
                  label={site.status}
                  color={site.status === 'Operating' ? 'primary' : 'default'}
                  size="extra-small"
                  chipType="thin"
                />
              ),
              single: true,
              inline: true,
            },
          ]
        : []),
      {
        key: 'name',
        label: 'Site name',
        value: <LabeledIcon label={site?.siteName ?? ''} icon="IconMonitor" />,
      },
      {
        key: 'siteID',
        label: 'Site ID',
        value: site?.siteId.toString(),
      },
      {
        key: 'customerName',
        label: 'Customer name',
        value: <LabeledIcon label={site?.customerName ?? ''} icon="IconUser" />,
      },
      {
        key: 'type',
        label: 'Site type',
        value: site?.type,
      },
      {
        key: 'location',
        label: 'Location',
        single: true,
        value: (
          <Stack direction="row" gap={8}>
            <LabeledIcon label={location} icon="IconHome" />
            <Flag
              flag={site?.country?.toLocaleLowerCase() as FlagsType}
              width={20}
            />
          </Stack>
        ),
      },
      ...(site?.notes || site?.comments
        ? [
            {
              key: 'notes',
              label: 'Notes',
              value: (
                <TextTruncate width="100%" showMoreText showLessText lines={2}>
                  {site.notes || site.comments}
                </TextTruncate>
              ),
              single: true,
            },
          ]
        : []),
    ],
    [site],
  );

  if (isLoading) {
    return (
      <div className="outage-site-loader">
        <LoadingSpinner
          text="Loading site..."
          size={36}
          color="var(--color-primary)"
          align="center"
          inline
        />
      </div>
    );
  }

  if (!site) {
    return <Typography tag="h3">Site not available</Typography>;
  }

  return (
    <div className="outage-site">
      <Stack direction="row" gap={8}>
        <Typography tag="textdefault_strong">Site</Typography>
        <Tooltip title={SITE_TOOLTIP_TEXT} arrow>
          <Icon
            icon="IconInformation"
            color="var(--color-default)"
            iconSize={0.8}
          />
        </Tooltip>
      </Stack>
      <DataItems items={dataItems} />
    </div>
  );
};
